<template>
  <section class="dept-hero dept-modern__section">
    <div class="dept-modern">
      <nav v-if="breadcrumbs.length" class="dept-hero__breadcrumbs" aria-label="Хлебные крошки">
        <ol class="dept-hero__breadcrumbs-list">
          <li
            v-for="(crumb, idx) in breadcrumbs"
            :key="idx"
            class="dept-hero__breadcrumbs-item"
          >
            <router-link
              v-if="crumb.link && idx < breadcrumbs.length - 1"
              :to="crumb.link"
              class="dept-hero__breadcrumbs-link"
            >
              {{ crumb.label }}
            </router-link>
            <span v-else class="dept-hero__breadcrumbs-current">{{ crumb.label }}</span>
            <span v-if="idx < breadcrumbs.length - 1" class="dept-hero__breadcrumbs-sep" aria-hidden="true">→</span>
          </li>
        </ol>
      </nav>

      <div class="dept-hero__card dept-modern__card">
        <div class="dept-hero__grid">
          <div class="dept-hero__content">
            <h1 v-if="title" class="dept-hero__title">{{ title }}</h1>
            <p v-if="description" class="dept-hero__description">{{ description }}</p>
            <button
              v-if="hasMore"
              type="button"
              class="dept-hero__more"
              @click="openAboutModal"
            >
              Подробнее
            </button>

            <div v-if="hasStats" class="dept-hero__about">
              <div class="dept-hero__stats">
                <div v-for="(stat, idx) in stats" :key="idx" class="dept-hero__stat">
                  <div class="dept-hero__stat-icon" aria-hidden="true">
                    <img
                      v-if="iconSrc(stat)"
                      class="dept-hero__stat-img"
                      :src="iconSrc(stat)"
                      alt=""
                    >
                    <svg v-else viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/>
                    </svg>
                  </div>
                  <span class="dept-hero__stat-label">{{ stat.label }}</span>
                </div>
              </div>
            </div>

            <div v-if="hasActions" class="dept-hero__actions">
              <router-link
                v-if="secondaryButton.text"
                :to="secondaryButton.link || '#'"
                class="dept-hero__btn dept-hero__btn--primary"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                {{ secondaryButton.text }}
              </router-link>
              <router-link
                v-if="reviewButton.text"
                :to="reviewButton.link || '#'"
                class="dept-hero__btn dept-hero__btn--outline"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 17.3l-5.2 2.7 1-5.8L3.6 9.9l5.8-.8L12 3.8l2.6 5.3 5.8.8-4.2 4.3 1 5.8L12 17.3z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                </svg>
                {{ reviewButton.text }}
              </router-link>
            </div>
          </div>

          <div class="dept-hero__image-wrap">
            <img
              v-if="imageUrl"
              :src="imageUrl"
              :alt="imageAlt || title"
              class="dept-hero__image"
            >
            <div v-else class="dept-hero__image-placeholder" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" width="48" height="48">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.4"/>
                <path d="M3 15l4.5-4.5L12 15l3-3 6 6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DepartmentAboutModal
      v-if="aboutModalOpen"
      :about-heading="aboutHeading"
      :about-text="aboutText"
      :gallery="gallery"
      @close="closeAboutModal"
    />
  </section>
</template>

<script>
import { departmentStatIconSrc } from '@/helpers/departmentStatIcons'
import DepartmentAboutModal from '@/components/Departments/DepartmentAboutModal.vue'

export default {
  name: 'DepartmentHero',
  components: { DepartmentAboutModal },
  props: {
    breadcrumbs: { type: Array, default: () => [] },
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    secondaryButton: { type: Object, default: () => ({ text: '', link: '' }) },
    reviewButton: { type: Object, default: () => ({ text: '', link: '' }) },
    image: { type: String, default: '' },
    imageAlt: { type: String, default: '' },
    aboutHeading: { type: String, default: '' },
    aboutText: { type: String, default: '' },
    gallery: { type: Array, default: () => [] },
    stats: { type: Array, default: () => [] },
  },
  data() {
    return {
      aboutModalOpen: false,
    }
  },
  computed: {
    imageUrl() {
      return String(this.image || '').trim()
    },
    hasStats() {
      return !!(this.stats && this.stats.length)
    },
    hasMore() {
      const text = String(this.aboutText || '').trim()
      const gallery = Array.isArray(this.gallery) ? this.gallery : []
      const hasPhoto = gallery.some((item) => String((item && item.image) || '').trim())
      return !!(text || hasPhoto)
    },
    hasActions() {
      return !!(this.secondaryButton && this.secondaryButton.text)
        || !!(this.reviewButton && this.reviewButton.text)
    },
  },
  methods: {
    iconSrc: departmentStatIconSrc,
    openAboutModal() {
      this.aboutModalOpen = true
    },
    closeAboutModal() {
      this.aboutModalOpen = false
    },
  },
}
</script>

<style scoped lang="scss">
$green-text: #336633;
$text: #2d3436;
$muted: #6b7280;

.dept-hero__breadcrumbs {
  margin-bottom: 16px;
}

.dept-hero__breadcrumbs-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.dept-hero__breadcrumbs-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "Circe", sans-serif;
  font-size: 14px;
}

.dept-hero__breadcrumbs-link {
  color: $muted;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: $green-text;
  }
}

.dept-hero__breadcrumbs-current {
  color: $muted;
}

.dept-hero__breadcrumbs-sep {
  color: #b0b8b0;
}

.dept-hero__card {
  overflow: hidden;
}

.dept-hero__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: stretch;
  min-height: 420px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    min-height: 0;
  }
}

.dept-hero__content {
  display: flex;
  flex-direction: column;
  padding: 36px 40px;
  box-sizing: border-box;

  @media (max-width: 767.98px) {
    padding: 24px 20px;
  }
}

.dept-hero__title {
  margin: 0 0 14px;
  font-family: "TTDrugs", sans-serif;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.15;
  color: $green-text;

  @media (max-width: 767.98px) {
    font-size: 26px;
  }
}

.dept-hero__description {
  margin: 0 0 12px;
  font-family: "Circe", sans-serif;
  font-size: 17px;
  line-height: 1.55;
  color: $muted;
}

.dept-hero__more {
  align-self: flex-start;
  margin: 0 0 18px;
  padding: 0;
  border: none;
  background: none;
  font-family: "Circe", sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: $green-text;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    color: #244a20;
  }

  &:focus-visible {
    outline: 2px solid #2d5a27;
    outline-offset: 2px;
  }
}

.dept-hero__about {
  margin-top: 4px;
  padding-top: 20px;
  border-top: 1px solid #e8ece8;
}

.dept-hero__stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
  margin-bottom: 8px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.dept-hero__stat {
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
}

.dept-hero__stat-icon {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  color: #66cc66;

  svg {
    width: 28px;
    height: 28px;
  }
}

.dept-hero__stat-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.dept-hero__stat-label {
  font-family: "Circe", sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: $text;
}

.dept-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: auto;
  padding-top: 24px;
}

/* Кнопки как на /home-redesign-a: primary = «Записаться», outline = «О центре» */
$hero-btn-dark: #2d5a27;
$hero-btn-soft: #e8f5e9;

.dept-hero__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 18px;
  border-radius: 16px;
  box-sizing: border-box;
  font-family: "Circe", sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease,
    color 0.25s ease, border-color 0.25s ease;
  border: 1.5px solid transparent;
  appearance: none;
  -webkit-appearance: none;

  &:hover {
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid $hero-btn-dark;
    outline-offset: 2px;
  }

  svg {
    flex-shrink: 0;
  }

  &--primary {
    background: $hero-btn-dark;
    color: #fff;
    border-color: $hero-btn-dark;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);

    &:hover {
      background: #244a20;
      border-color: #244a20;
      color: #fff;
    }
  }

  &--outline {
    background: transparent;
    color: $hero-btn-dark;
    border-color: $hero-btn-dark;

    &:hover {
      background: $hero-btn-soft;
      color: $hero-btn-dark;
    }
  }
}

.dept-hero__image-wrap {
  position: relative;
  min-height: 320px;
  background: #eef3ee;
  overflow: hidden;

  @media (max-width: 900px) {
    min-height: 260px;
    order: -1;
  }
}

.dept-hero__image {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 320px;
  object-fit: cover;
  object-position: center;

  @media (max-width: 900px) {
    min-height: 260px;
  }
}

.dept-hero__image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 320px;
  color: #9bb09c;
  background: linear-gradient(145deg, #f4f8f4 0%, #e4ece4 100%);
}
</style>
